import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, ArrowLeft } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiErrorDetail } from "@/lib/api";

const inputCls =
    "bg-abyss border-border rounded-none h-12 text-foreground placeholder:text-steel/60 focus-visible:ring-safety focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:border-safety";

export default function Login() {
    const { login } = useAuth();
    const navigate = useNavigate();
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setError("");
        setLoading(true);
        try {
            await login(email, password);
            navigate("/admin");
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || "Login failed.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-abyss grain flex items-center justify-center px-6">
            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
                className="w-full max-w-md border border-border bg-surface p-10 clip-plate"
            >
                <Link
                    to="/"
                    data-testid="back-home"
                    className="inline-flex items-center gap-2 font-mono-tech text-[11px] uppercase tracking-[0.2em] text-steel hover:text-safety transition-colors mb-8"
                >
                    <ArrowLeft className="h-3.5 w-3.5" /> Back to site
                </Link>

                <div className="flex items-center gap-3 mb-8">
                    <span className="h-9 w-9 bg-safety flex items-center justify-center font-heading font-black text-white text-lg">
                        K
                    </span>
                    <span className="font-heading font-extrabold tracking-tight text-lg">
                        ADMIN PORTAL
                    </span>
                </div>

                <h1 className="font-heading font-black tracking-tighter text-3xl mb-2">
                    Secure Access
                </h1>
                <p className="text-steel text-sm mb-8">
                    Restricted to Keva Service operations staff.
                </p>

                <form onSubmit={submit} data-testid="login-form" className="space-y-5">
                    <div className="space-y-2">
                        <Label className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-steel">
                            Email
                        </Label>
                        <Input data-testid="login-email" type="email" className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@kevaservice.com" />
                    </div>
                    <div className="space-y-2">
                        <Label className="font-mono-tech text-[11px] uppercase tracking-[0.2em] text-steel">
                            Password
                        </Label>
                        <Input data-testid="login-password" type="password" className={inputCls} value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" />
                    </div>

                    {error && (
                        <p data-testid="login-error" className="text-sm text-safety font-mono-tech">
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        data-testid="login-submit"
                        disabled={loading}
                        className="w-full inline-flex items-center justify-center gap-2 bg-safety hover:bg-safety-hover disabled:opacity-60 text-white font-mono-tech text-xs uppercase tracking-[0.15em] px-8 py-4 transition-colors"
                    >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Authenticate"}
                    </button>
                </form>
            </motion.div>
        </div>
    );
}
